import type {
  CardFieldElementType,
  ExpressCheckoutElementOptions,
  PaypercutElementsOptions,
  PaypercutOptions,
} from '../types/elements';
import { CARD_COLLECTION_PROTOCOL_VERSION } from './card-collection-provider';

const FRAME_URL_LENGTH_WARNING_THRESHOLD = 4_096;
let hasWarnedAboutLongFrameUrl = false;
export const ELEMENTS_FRAME_NAVIGATION_QUERY = '__paypercut_navigation';

type ControllerFrameUrlInput = {
  hostedOrigin: string;
  controllerId: string;
  clientOptions: PaypercutOptions;
  elementsOptions: PaypercutElementsOptions;
  sdkVersion: string;
  referrer: string;
};

type ExpressCheckoutFrameUrlInput = {
  hostedOrigin: string;
  controllerId: string;
  componentId: string;
  clientOptions: PaypercutOptions;
  elementsOptions: PaypercutElementsOptions;
  options: ExpressCheckoutElementOptions;
  referrer: string;
};

type CardElementFrameUrlInput = {
  hostedOrigin: string;
  controllerId: string;
  componentId: string;
  clientOptions: PaypercutOptions;
  elementsOptions: PaypercutElementsOptions;
  referrer: string;
};

type CardFieldFrameUrlInput = CardElementFrameUrlInput & {
  type: CardFieldElementType;
};

/**
 * Build public, immutable iframe bootstrap state.
 *
 * The hash is deliberately not an authorization channel: the authenticated
 * MessageChannel configuration remains authoritative. It lets a component
 * choose the smallest provider bundle and paint the right skeleton before the
 * controller has completed its API bootstrap, without sending the values in
 * the iframe HTTP request or Referer header.
 */
export function buildElementsControllerFrameUrl(input: ControllerFrameUrlInput): string {
  const params = new URLSearchParams();
  setSharedParams(params, input);
  params.set('componentName', 'controller');
  params.set('sdkVersion', input.sdkVersion);
  params.set('mode', input.elementsOptions.mode);
  params.set('amount', String(input.elementsOptions.amount));
  params.set('currency', input.elementsOptions.currency);
  params.set('cardCollectionProtocol', String(CARD_COLLECTION_PROTOCOL_VERSION));
  return withFragment('/elements/controller', input.hostedOrigin, params);
}

export function withElementsFrameNavigationIdentity(
  frameUrl: string,
  navigationId: string,
): string {
  const url = new URL(frameUrl);
  url.searchParams.set(ELEMENTS_FRAME_NAVIGATION_QUERY, navigationId);
  return url.toString();
}

export function buildExpressCheckoutFrameUrl(input: ExpressCheckoutFrameUrlInput): string {
  const params = new URLSearchParams();
  setSharedParams(params, input);
  params.set('componentName', 'expressCheckout');
  params.set('elementId', input.componentId);
  params.set('mode', input.elementsOptions.mode);
  params.set('amount', String(input.elementsOptions.amount));
  params.set('currency', input.elementsOptions.currency);

  set(params, 'publicOptions[paymentMethods][applePay]', input.options.paymentMethods?.applePay);
  set(params, 'publicOptions[paymentMethods][googlePay]', input.options.paymentMethods?.googlePay);
  set(params, 'publicOptions[layout][maxColumns]', input.options.layout?.maxColumns);
  set(params, 'publicOptions[layout][maxRows]', input.options.layout?.maxRows);
  set(params, 'publicOptions[layout][overflow]', input.options.layout?.overflow);
  set(params, 'publicOptions[buttonType][applePay]', input.options.buttonType?.applePay);
  set(params, 'publicOptions[buttonType][googlePay]', input.options.buttonType?.googlePay);
  set(params, 'publicOptions[buttonTheme][applePay]', input.options.buttonTheme?.applePay);
  set(params, 'publicOptions[buttonTheme][googlePay]', input.options.buttonTheme?.googlePay);
  set(params, 'publicOptions[buttonHeight]', input.options.buttonHeight);
  set(params, 'publicOptions[buttonRadius]', input.options.buttonRadius);
  set(params, 'publicOptions[emailRequired]', input.options.emailRequired);
  set(params, 'publicOptions[phoneNumberRequired]', input.options.phoneNumberRequired);
  set(params, 'publicOptions[billingAddressRequired]', input.options.billingAddressRequired);
  set(params, 'publicOptions[shippingAddressRequired]', input.options.shippingAddressRequired);
  if (input.options.allowedShippingCountries?.length) {
    params.set(
      'publicOptions[allowedShippingCountries]',
      input.options.allowedShippingCountries.join(','),
    );
  }
  return withFragment('/elements/components/express-checkout', input.hostedOrigin, params);
}

export function buildCardElementFrameUrl(input: CardElementFrameUrlInput): string {
  const params = new URLSearchParams();
  setSharedParams(params, input);
  params.set('componentName', 'card');
  params.set('elementId', input.componentId);
  params.set('mode', input.elementsOptions.mode);
  params.set('amount', String(input.elementsOptions.amount));
  params.set('currency', input.elementsOptions.currency);

  return withFragment('/elements/components/card', input.hostedOrigin, params);
}

export function buildCardFieldFrameUrl(input: CardFieldFrameUrlInput): string {
  const params = new URLSearchParams();
  setSharedParams(params, input, false);
  params.set('componentName', input.type);
  params.set('elementId', input.componentId);
  params.set('mode', input.elementsOptions.mode);
  params.set('amount', String(input.elementsOptions.amount));
  params.set('currency', input.elementsOptions.currency);

  const path =
    input.type === 'cardNumber'
      ? '/elements/components/card-number'
      : input.type === 'cardExpiry'
        ? '/elements/components/card-expiry'
        : '/elements/components/card-cvc';
  return withFragment(path, input.hostedOrigin, params);
}

function setSharedParams(
  params: URLSearchParams,
  input: {
    controllerId: string;
    clientOptions: PaypercutOptions;
    elementsOptions: PaypercutElementsOptions;
    referrer: string;
  },
  includeKeyMode = true,
): void {
  params.set('controllerId', input.controllerId);
  params.set('apiKey', input.clientOptions.publishableKey);
  set(params, 'account', input.clientOptions.account);
  if (includeKeyMode) params.set('keyMode', inferKeyMode(input.clientOptions.publishableKey));
  params.set('locale', input.elementsOptions.locale || navigatorLocale());
  params.set('referrer', input.referrer);
  setAppearanceParams(params, input.elementsOptions.appearance);
}

function setAppearanceParams(
  params: URLSearchParams,
  appearance: PaypercutElementsOptions['appearance'],
): void {
  set(params, 'publicOptions[appearance][theme]', appearance?.theme);
  set(params, 'publicOptions[appearance][inputs]', appearance?.inputs);
  set(params, 'publicOptions[appearance][labels]', appearance?.labels);
  set(params, 'publicOptions[appearance][disableAnimations]', appearance?.disableAnimations);

  for (const [name, value] of Object.entries(appearance?.variables ?? {}).sort(([a], [b]) =>
    a.localeCompare(b),
  )) {
    set(params, `publicOptions[appearance][variables][${name}]`, value);
  }

  for (const [selector, declarations] of Object.entries(appearance?.rules ?? {}).sort(([a], [b]) =>
    a.localeCompare(b),
  )) {
    for (const [property, value] of Object.entries(declarations ?? {}).sort(([a], [b]) =>
      a.localeCompare(b),
    )) {
      set(params, `publicOptions[appearance][rules][${selector}][${property}]`, value);
    }
  }
}

function set(
  params: URLSearchParams,
  name: string,
  value: string | number | boolean | undefined,
): void {
  if (value !== undefined) params.set(name, String(value));
}

function withFragment(path: string, hostedOrigin: string, params: URLSearchParams): string {
  const url = new URL(path, hostedOrigin);
  url.hash = params.toString();
  const built = url.toString();
  if (!hasWarnedAboutLongFrameUrl && built.length > FRAME_URL_LENGTH_WARNING_THRESHOLD) {
    hasWarnedAboutLongFrameUrl = true;
    if (typeof console !== 'undefined' && typeof console.warn === 'function') {
      console.warn(
        `[Paypercut Elements] Generated iframe URL is ${built.length} characters. ` +
          'Consider shortening appearance variables or rules to avoid URL length limits.',
      );
    }
  }
  return built;
}

function inferKeyMode(publishableKey: string): 'live' | 'test' {
  return /(?:^|_)live(?:_|$)/i.test(publishableKey) ? 'live' : 'test';
}

function navigatorLocale(): string {
  return typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en';
}
